import React, { FC, useEffect } from 'react';
import defaultsDeep from 'lodash/defaultsDeep';

import { config, yup } from 'data';
import { formatter } from 'helpers';
import { withdrawalService } from 'services';
import { useForm, useFormWatch, useLang, useMutation, useQueryInvalidate } from 'hooks';
import { Clock } from 'components/icons';
import { CardView } from 'components/layout';
import { Button, Flex, Form, Input, Select } from 'components/ui';
import { Withdrawal, WithdrawalMethod, WithdrawalSchedule } from 'types/models';
import { WithdrawalParams } from 'types/services';

const withdrawalMethods = Object.values(WithdrawalMethod);

type FormValues = WithdrawalParams;

const initialValues: FormValues = {
  withdrawalMethod: WithdrawalMethod.BANK_TRANSFER,
  details: null,
};

const bankTransferValidationSchema = {
  bankCode: yup
    .string()
    .required()
    .min(config.BANK_CODE_MIN_LENGTH)
    .max(config.BANK_CODE_MAX_LENGTH),
  accountNumber: yup
    .string()
    .required()
    .min(config.ACCOUNT_NUMBER_MIN_LENGTH)
    .max(config.ACCOUNT_NUMBER_MAX_LENGTH),
};

const validationSchema = yup.object({
  withdrawalMethod: yup.string().required().oneOf(withdrawalMethods),
  details: yup
    .object()
    .required()
    .when('withdrawalMethod', ([method], schema) => {
      if (method === WithdrawalMethod.BANK_TRANSFER) {
        return schema.shape(bankTransferValidationSchema);
      }

      return schema;
    }),
});

type WithdrawalMethodProps = {
  withdrawal: Withdrawal;
};

const WithdrawalSettings: FC<WithdrawalMethodProps> = ({ withdrawal }) => {
  const form = useForm<FormValues>();
  const lang = useLang();
  const queryInvalidate = useQueryInvalidate();

  const currentMethod = useFormWatch('withdrawalMethod', form);

  const invalidateWithdrawalQueries = async () => {
    await queryInvalidate([config.WITHDRAWAL_QUERY_KEY]);
  };

  const updateWithdrawalMutation = useMutation({
    mutationFn: withdrawalService.updateWithdrawal,
    onSuccess: invalidateWithdrawalQueries,
    successNotification: lang.get('withdrawal.settings.success'),
  });

  const handleSubmit = async (values: FormValues) => {
    await updateWithdrawalMutation.mutateAsync(values);

    return false;
  };

  useEffect(() => {
    form.setFieldsValueWithoutTouching(defaultsDeep({}, withdrawal, initialValues));
  }, [withdrawal, form]);

  useEffect(() => {
    if (form.isFieldTouched('withdrawalMethod')) {
      form.resetFields(['details']);
    }
  }, [form, currentMethod]);

  return (
    <CardView
      title={lang.get('withdrawal.settings.title')}
      caption={lang.get('withdrawal.settings.caption')}
    >
      <Form
        form={form}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onFinish={handleSubmit}
      >
        <Form.Item name="withdrawalMethod" label={lang.get('common.form.withdrawalMethod.label')}>
          <Select
            placeholder={lang.get('common.form.withdrawalMethod.placeholder')}
            options={withdrawalMethods.map((method) => ({
              value: method,
              label: lang.get(`withdrawal.methods.${method.toLowerCase()}`),
            }))}
          />
        </Form.Item>
        <Form.Item
          name={['details', 'bankCode']}
          label={lang.get('common.form.bankCode.label')}
          hidden={currentMethod !== WithdrawalMethod.BANK_TRANSFER}
        >
          <Input placeholder={lang.get('common.form.bankCode.placeholder')} />
        </Form.Item>
        <Form.Item
          name={['details', 'accountNumber']}
          label={lang.get('common.form.accountNumber.label')}
          hidden={currentMethod !== WithdrawalMethod.BANK_TRANSFER}
        >
          <Input placeholder={lang.get('common.form.accountNumber.placeholder')} />
        </Form.Item>
        <Form.Item
          name="schedule"
          label={lang.get('common.form.withdrawalSchedule.label')}
          extra={lang.get('common.form.withdrawalSchedule.helper', {
            amount: formatter.formatCurrency(config.WITHDRAWAL_AMOUNT_MIN, config.DEFAULT_CURRENCY),
          })}
        >
          <Select
            placeholder={lang.get('common.form.withdrawalSchedule.placeholder')}
            options={Object.values(WithdrawalSchedule).map((schedule) => ({
              value: schedule,
              label: lang.get(`withdrawal.schedules.${schedule.toLowerCase()}`),
            }))}
            prefix={<Clock />}
            disabled
          />
        </Form.Item>
        <Flex justify="flex-end">
          <Button htmlType="submit" loading={updateWithdrawalMutation.isPending}>
            {lang.get('common.actions.save')}
          </Button>
        </Flex>
      </Form>
    </CardView>
  );
};

export default WithdrawalSettings;
